<template>
  <div class="portal-home-font-2x">
    <section id="home" class="relative h-[921px] w-full overflow-hidden flex items-center pt-20">
      <div class="absolute inset-0 z-0">
        <img alt="建筑愿景" class="w-full h-full object-cover" src="@/assets/img/code/img_0.jpg">
        <div class="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary-container/40"></div>
      </div>
      <div class="relative z-10 max-w-[1440px] mx-auto px-8 w-full">
        <div class="max-w-3xl space-y-6">
          <span class="text-secondary-container font-label tracking-widest text-sm font-bold uppercase">卓越不止 创新无界</span>
          <h1 class="text-white font-headline text-6xl font-extrabold leading-tight tracking-tight">
            四川 <span class="text-secondary-container">企联企业管理创新工作平台</span>
          </h1>
          <p class="text-surface-container-low text-lg leading-relaxed max-w-xl">
            协会致力于在数字化时代整合管理创新、评估体系与企业成长，推动行业转型升级。
          </p>
          <div class="portal-shrink-hero-actions flex gap-4 pt-4">
            <button class="portal-shrink-hero-button bg-secondary text-white px-8 py-4 rounded-lg font-bold flex items-center gap-2 hover:-translate-y-1 transition-transform" type="button" @click="scrollToSection('news')">
              探索<span class="material-symbols-outlined">arrow_forward</span>
            </button>
            <button class="portal-shrink-hero-button border-2 border-white/30 text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-colors backdrop-blur-sm" type="button" @click="goContestHome">
              人人都是创新官
            </button>
          </div>
        </div>
      </div>
    </section>

    <section id="about" class="py-24 bg-surface px-8">
      <div class="max-w-[1440px] mx-auto">
        <div class="mb-16">
          <h2 class="text-primary font-headline text-3xl font-bold border-l-4 border-secondary pl-4">关于我们</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div
            v-for="item in aboutDepartments"
            :key="item.slug"
            class="group bg-surface-container-lowest p-10 shadow-sm border border-transparent hover:border-secondary/20 hover:-translate-y-2 transition-all duration-300"
          >
            <div class="portal-shrink-icon-circle w-16 h-16 bg-surface-container-low flex items-center justify-center rounded-xl mb-8 group-hover:bg-secondary-container transition-colors">
              <span class="material-symbols-outlined text-primary text-3xl">{{ item.icon }}</span>
            </div>
            <h3 class="text-2xl font-bold text-primary mb-4">{{ item.title }}</h3>
            <p class="text-on-surface-variant leading-relaxed">{{ item.summary }}</p>
            <a class="mt-8 flex items-center text-secondary font-bold gap-2 group-hover:underline" href="#" @click.prevent="goAboutDetail(item.slug)">
              了解更多 <span class="material-symbols-outlined text-sm">open_in_new</span>
            </a>
          </div>
        </div>
      </div>
    </section>

    <section id="news" class="py-24 bg-surface-container-low px-8">
      <div class="max-w-[1440px] mx-auto">
        <div class="flex flex-col md:flex-row gap-12">
          <div class="md:w-1/3">
            <div class="flex items-center justify-between mb-8">
              <h2 class="text-primary font-headline text-4xl font-extrabold">新闻公告中心</h2>
              <a class="text-secondary font-bold text-sm flex items-center gap-1 hover:underline transition-all" href="#" @click.prevent="goContentList(activeNewsCategory)">
                查看更多 <span class="material-symbols-outlined text-xs">arrow_forward</span>
              </a>
            </div>
            <div class="flex flex-col gap-2">
              <button :class="newsTabClass('news')" class="text-left px-6 py-4 rounded-lg transition-all flex items-center justify-between" type="button" @click="activeNewsCategory = 'news'">
                协会新闻 <span class="material-symbols-outlined">chevron_right</span>
              </button>
              <button :class="newsTabClass('notice')" class="text-left px-6 py-4 rounded-lg transition-all flex items-center justify-between" type="button" @click="activeNewsCategory = 'notice'">
                官方公告 <span class="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          </div>
          <div class="md:w-2/3 space-y-6">
            <div v-if="newsLoading" class="bg-surface-container-lowest p-8 rounded-lg shadow-sm text-on-surface-variant">正在加载内容...</div>
            <div v-else-if="displayedPortalItems.length === 0" class="bg-surface-container-lowest p-8 rounded-lg shadow-sm text-on-surface-variant">暂时没有可展示内容</div>
            <article
              v-for="item in displayedPortalItems"
              :key="item.contentId"
              class="bg-surface-container-lowest p-8 rounded-lg shadow-sm group cursor-pointer hover:bg-surface-bright transition-colors border-l-4 border-transparent hover:border-secondary"
              @click="openPortalItem(item)"
            >
              <div class="flex justify-between items-start mb-4">
                <span class="text-secondary font-label font-bold text-xs">{{ formatPortalDate(item.publishTime) }}</span>
                <span class="bg-surface-container-low text-primary px-3 py-1 rounded text-xs font-bold">{{ activeNewsCategory === 'notice' ? '官方公告' : '协会新闻' }}</span>
              </div>
              <h3 class="text-2xl font-bold text-primary mb-3 group-hover:text-secondary transition-colors">{{ item.title }}</h3>
              <p class="text-on-surface-variant line-clamp-2 leading-relaxed">{{ item.summary || '暂无摘要' }}</p>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section class="py-24 bg-surface-container-lowest px-8">
      <div class="max-w-[1440px] mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div id="experts">
            <div class="flex items-center justify-between mb-8">
              <h2 class="text-primary font-headline text-3xl font-bold border-l-4 border-secondary pl-4">专家智库</h2>
              <a class="text-secondary font-bold text-sm flex items-center gap-1 hover:underline transition-all" href="#" @click.prevent="$router.push('/expert/list')">
                查看更多 <span class="material-symbols-outlined text-xs">arrow_forward</span>
              </a>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div v-if="expertLoading" class="col-span-2 text-center py-8 text-on-surface-variant text-sm">加载中...</div>
              <div v-else-if="expertProfiles.length === 0" class="col-span-2 text-center py-8 text-on-surface-variant text-sm">暂无专家信息</div>
              <div v-for="expert in expertProfiles" :key="expert.id" class="bg-surface-container-low p-6 rounded-xl shadow-sm border border-transparent hover:border-secondary/30 transition-all flex flex-col items-center text-center gap-4 group cursor-pointer" @click="$router.push('/expert/' + expert.id)">
                <div class="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-2xl overflow-hidden border-4 border-white shadow-sm group-hover:scale-105 transition-transform">
                  <img v-if="expert.avatar" :alt="expert.name" :src="expert.avatar" class="w-full h-full object-cover" />
                  <span v-else>{{ (expert.name || '专').charAt(0) }}</span>
                </div>
                <div>
                  <h4 class="text-lg font-bold text-primary group-hover:text-secondary transition-colors">{{ expert.name }}</h4>
                  <p class="text-sm text-secondary font-semibold mb-2">{{ expert.title }}</p>
                  <p class="text-xs text-on-surface-variant line-clamp-2">{{ expert.summary }}</p>
                </div>
              </div>
            </div>
          </div>

          <div id="support">
            <div class="flex items-center justify-between mb-8">
              <h2 class="text-primary font-headline text-3xl font-bold border-l-4 border-secondary pl-4">智力支持</h2>
              <a class="text-secondary font-bold text-sm flex items-center gap-1 hover:underline transition-all" href="#" @click.prevent="$router.push('/support/list')">
                查看更多 <span class="material-symbols-outlined text-xs">arrow_forward</span>
              </a>
            </div>
            <div class="space-y-6">
              <div v-for="support in supportProjects" :key="support.title" class="bg-surface-container-low p-6 rounded-xl shadow-sm border border-transparent hover:border-secondary/30 transition-all group flex flex-col sm:flex-row gap-6 cursor-pointer" @click="$router.push('/support/1')">
                <div class="w-full sm:w-32 h-24 bg-surface-container-highest rounded-lg overflow-hidden shrink-0">
                  <img :alt="support.title" :src="support.image" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500">
                </div>
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-2">
                    <span :class="support.tagClass">{{ support.tag }}</span>
                  </div>
                  <h4 class="text-lg font-bold text-primary group-hover:text-secondary transition-colors mb-2 line-clamp-1">{{ support.title }}</h4>
                  <p class="text-sm text-on-surface-variant line-clamp-2">{{ support.summary }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="case-center" class="py-24 bg-surface px-8">
      <div class="max-w-[1440px] mx-auto">
        <div class="flex items-center justify-between mb-12">
          <h2 class="text-primary font-headline text-3xl font-bold border-l-4 border-secondary pl-4">案例中心</h2>
          <a class="text-secondary font-bold text-sm flex items-center gap-1 hover:underline" href="#" @click.prevent="goCaseCenterList">
            查看更多 <span class="material-symbols-outlined text-xs">arrow_forward</span>
          </a>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <a v-for="item in caseStudies" :key="item.id || item.title" class="group relative block aspect-[4/5] overflow-hidden rounded-2xl shadow-xl transition-transform duration-500 hover:-translate-y-2" href="#" @click.prevent="goCaseCenterDetail(item.id)">
            <img :alt="item.title" :src="item.image" class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110">
            <div class="case-card-overlay absolute inset-0 flex flex-col justify-end p-8">
              <span class="text-secondary font-bold text-xs uppercase tracking-widest mb-2">{{ item.category }}</span>
              <h4 class="text-white font-headline text-2xl font-bold leading-tight group-hover:text-secondary transition-colors">{{ item.title }}</h4>
              <div class="flex items-center gap-2 mt-4 text-white/70 text-sm font-medium transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                查看项目详情 <span class="material-symbols-outlined text-base">arrow_forward</span>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>

    <section id="database" class="py-24 bg-surface-container-lowest px-8">
      <div class="max-w-[1440px] mx-auto">
        <div class="flex items-center justify-between mb-12">
          <h2 class="text-primary font-headline text-3xl font-bold border-l-4 border-secondary pl-4">创新成果和管理论文数据库</h2>
          <a class="text-secondary font-bold text-sm flex items-center gap-1 hover:underline" href="#" @click.prevent="$router.push('/database')">
            查看更多 <span class="material-symbols-outlined text-xs">arrow_forward</span>
          </a>
        </div>
        <div class="flex gap-8 border-b border-surface-dim mb-8">
          <button
            v-for="tab in databaseTabs"
            :key="tab.key"
            :class="databaseTabClass(tab.key)"
            type="button"
            @click="activeDatabaseTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full table-fixed text-left border-collapse">
            <thead>
              <tr class="bg-surface-container-low text-primary font-bold text-sm">
                <th class="p-4 rounded-tl-lg w-24">年份</th>
                <th class="p-4 w-[38%]">{{ activeDatabaseTab === 'management' ? '创新成果名称' : '论文题目' }}</th>
                <th class="p-4 w-48">{{ activeDatabaseTab === 'management' ? '管理范畴' : '主题类别' }}</th>
                <th class="p-4 w-72">{{ activeDatabaseTab === 'management' ? '企业名称' : '作者' }}</th>
                <th class="p-4 rounded-tr-lg w-32 text-center">操作</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-surface-dim">
              <tr v-if="currentDatabaseEntries.length === 0">
                <td class="p-6 text-center text-sm text-on-surface-variant" colspan="5">暂无数据</td>
              </tr>
              <tr v-for="item in currentDatabaseEntries" :key="`${activeDatabaseTab}-${item.id || item.title}`" class="hover:bg-surface-container-low/50 transition-colors group">
                <td class="p-4 text-sm text-on-surface-variant">{{ item.year }}</td>
                <td class="p-4">
                  <span class="block font-bold text-primary group-hover:text-secondary transition-colors leading-relaxed line-clamp-2 min-h-[3.5rem]">{{ item.title }}</span>
                </td>
                <td class="p-4 text-sm text-on-surface-variant align-top">
                  <span class="block truncate">{{ item.category || '-' }}</span>
                </td>
                <td class="p-4 text-sm text-on-surface-variant align-top">
                  <span class="block truncate">{{ activeDatabaseTab === 'management' ? (item.companyName || '-') : (item.author || '-') }}</span>
                </td>
                <td class="p-4 text-center align-top">
                  <a class="text-secondary font-bold text-sm hover:underline flex items-center justify-center gap-1" href="#" @click.prevent="handleOpenDatabaseDetail(item)">
                    查看全文 <span class="material-symbols-outlined text-[16px]">open_in_new</span>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section id="journal" class="py-24 bg-surface px-8">
      <div class="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div class="lg:col-span-1">
          <div class="flex items-center justify-between mb-8">
            <h2 class="text-primary font-headline text-3xl font-bold border-l-4 border-secondary pl-4">期刊</h2>
            <a class="text-secondary font-bold text-sm flex items-center gap-1 hover:underline" href="#" @click.prevent="goJournalList">
              查看更多 <span class="material-symbols-outlined text-xs">arrow_forward</span>
            </a>
          </div>
          <h3 class="text-xl font-bold text-primary mb-8 font-headline">《经营管理者》</h3>
          <div class="relative mt-8 mb-12">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-8 relative z-10 px-4">
              <div v-for="issue in journalIssues" :key="issue.id" class="group relative w-full aspect-[3/4] cursor-pointer shadow-md hover:-translate-y-4 hover:shadow-2xl transition-all duration-300 bg-white" @click="goJournalDetail(issue.id)">
                <img :alt="issue.issue" :src="issue.image" class="w-full h-full object-cover border-l-4 border-surface-dim">
                <div class="absolute inset-0 bg-primary/90 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                  <div class="text-center p-4">
                    <span class="text-secondary font-bold text-sm block mb-2">{{ issue.issue }}</span>
                    <span class="text-white text-xs block border border-white/30 rounded px-2 py-1 mt-2 hover:bg-white/10 transition-colors">在线阅读</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="bookshelf-shelf"></div>
          </div>
        </div>

        <div class="lg:col-span-2 flex flex-col">
          <div class="flex items-center justify-between mb-8">
            <h2 class="text-primary font-headline text-3xl font-bold border-l-4 border-secondary pl-4">合作伙伴</h2>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1">
            <a v-for="ad in enterpriseCards" :key="ad.id || ad.title" class="block relative rounded-2xl overflow-hidden group shadow-lg h-full" href="#" @click.prevent="openEnterpriseItem(ad)">
              <img :alt="`${ad.title} 占位图`" :src="ad.placeholderImage" class="absolute inset-0 w-full h-full object-cover">
              <img
                v-if="ad.isImageReady"
                :alt="ad.title"
                :src="ad.displayImage"
                :class="[
                  'absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-105',
                  ad.isImageReady ? 'opacity-100' : 'opacity-0'
                ]"
              >
              <div class="absolute inset-0 bg-gradient-to-t from-primary via-primary/50 to-transparent flex flex-col justify-end p-6">
                <span class="bg-secondary text-white text-[10px] font-bold px-2 py-0.5 rounded inline-block mb-3 w-max uppercase tracking-wider">{{ ad.tag }}</span>
                <h4 class="text-white font-headline text-xl font-bold mb-2 leading-tight group-hover:text-secondary transition-colors">{{ ad.title }}</h4>
                <div class="flex items-center gap-2 text-secondary font-bold text-xs">
                  {{ ad.action }} <span class="material-symbols-outlined text-sm">arrow_forward</span>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>

    <portal-footer @scroll-to="scrollToSection" />
    <div v-if="showMemberDialog" class="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
      <div class="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div class="px-8 py-6 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h3 class="text-2xl font-bold text-primary">{{ sessionInfo.token ? '输入会员码' : '查看全文' }}</h3>
            <p class="text-sm text-slate-500 mt-2">
              {{ sessionInfo.token ? '请输入会员码后查看全文，姓名和联系方式非必填。' : '查看创新成果和管理论文全文需先验证会员码。' }}
            </p>
          </div>
          <button type="button" class="w-10 h-10 -mr-2 -mt-1 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" @click="closeMemberDialog">
            <span class="material-symbols-outlined text-xl">close</span>
          </button>
        </div>
        <div class="px-8 py-6 space-y-4">
          <template v-if="!showMemberForm">
            <div class="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600 leading-7">
              仅支持历年进入榜单排名的创新成果和管理论文查看全文。
              同一浏览器验证成功后，会员有效期内无需重复输入。
              更换浏览器、清理缓存或使用无痕模式后，重新输入正确会员码即可继续访问。
            </div>
            <button @click="showMemberForm = true" class="w-full rounded-2xl bg-primary text-white py-3 font-semibold hover:opacity-95 transition-opacity">
              输入会员码
            </button>
          </template>
          <template v-else>
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">姓名（非必填）</label>
              <input v-model.trim="memberForm.userName" class="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:border-primary" placeholder="请输入姓名（非必填）">
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">联系方式（手机，非必填）</label>
              <input v-model.trim="memberForm.mobile" class="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:border-primary" placeholder="请输入手机号（非必填）">
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">会员码</label>
              <input v-model.trim="memberForm.memberCode" maxlength="8" class="w-full rounded-xl border border-slate-200 px-4 py-3 uppercase tracking-[0.3em] focus:outline-none focus:border-primary" placeholder="请输入8位会员码">
            </div>
            <p v-if="memberError" class="text-sm text-red-500">{{ memberError }}</p>
            <div class="flex gap-3 pt-2">
              <button @click="closeMemberDialog" class="flex-1 rounded-2xl border border-slate-200 py-3 font-medium text-slate-600">取消</button>
              <button @click="submitMemberCode" class="flex-1 rounded-2xl bg-primary text-white py-3 font-semibold hover:opacity-95 transition-opacity">登录</button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchPortalHomeContent, formatPortalDate, preloadPortalImage, resolvePortalFileUrlCandidates } from '@/utils/content'
import { fetchExpertList } from '@/expert/utils/expert'
import { fetchInnovationsHistoryList, fetchThesisHistoryList, fetchDatabaseDict, loginByMemberCode, MEMBER_CODE_STORAGE_KEY } from '@/utils/database'
import { getAboutDepartments } from '@/utils/about'
import { getHomeCaseCards } from '@/utils/caseCenter'
import { getHomeJournalIssues } from '@/utils/journal'
import PortalFooter from '@/components/PortalFooter.vue'

export default {
  name: 'Home',
  components: {
    PortalFooter
  },
  data () {
    return {
      activeNewsCategory: 'news',
      activeDatabaseTab: 'management',
      newsLoading: false,
      expertLoading: false,
      portalContent: {
        newsList: [],
        noticeList: [],
        enterpriseList: []
      },
      aboutDepartments: getAboutDepartments().map(item => ({
        slug: item.slug,
        icon: item.areas[0] ? item.areas[0].icon : 'info',
        title: item.title,
        summary: item.subtitle
      })),
      expertProfiles: [],
      supportProjects: [
        {
          tag: '咨询服务',
          tagClass: 'text-[10px] font-bold text-secondary bg-secondary/10 px-2 py-1 rounded tracking-wide',
          title: '企业数字化转型战略规划',
          summary: '为大型企业提供顶层设计、实施路径与组织协同机制咨询，提升战略落地效率。',
          image: require('@/assets/img/about-image1.jpg')
        },
        {
          tag: '学术资源',
          tagClass: 'text-[10px] font-bold text-primary bg-primary/10 px-2 py-1 rounded tracking-wide',
          title: '高质量发展研究白皮书',
          summary: '围绕产业趋势、管理创新与实践案例形成研究成果，为企业提供决策参考。',
          image: require('@/assets/img/about-image2.jpg')
        }
      ],
      caseStudies: getHomeCaseCards(),
      databaseTabs: [
        { key: 'management', label: '创新成果' },
        { key: 'paper', label: '管理论文' }
      ],
      managementCategoryOptions: [],
      databaseEntries: {
        management: [],
        paper: []
      },
      paperTopicTypeOptions: [],
      showMemberDialog: false,
      showMemberForm: false,
      pendingItem: null,
      memberError: '',
      sessionInfo: {},
      enterpriseCards: [],
      enterprisePreloadToken: 0,
      memberForm: {
        userName: '',
        mobile: '',
        memberCode: ''
      },
      journalIssues: getHomeJournalIssues(),
      enterpriseFallbackImages: [
        require('@/assets/img/code/img_9.jpg'),
        require('@/assets/img/code/img_10.jpg'),
        require('@/assets/img/code/img_11.jpg')
      ]
    }
  },
  computed: {
    displayedPortalItems () {
      return this.activeNewsCategory === 'notice' ? this.portalContent.noticeList : this.portalContent.newsList
    },
    currentDatabaseEntries () {
      return this.databaseEntries[this.activeDatabaseTab] || []
    }
  },
  mounted () {
    this.restoreMemberSession()
    this.loadPortalContent()
    this.loadExperts()
    this.loadManagementDatabase()
    this.loadPaperDatabase()
    this.scrollToRouteSection()
  },
  watch: {
    '$route.query.section' () {
      this.$nextTick(() => {
        this.scrollToRouteSection()
      })
    }
  },
  methods: {
    formatPortalDate,
    settlePromise (promise) {
      return promise
        .then(value => ({ status: 'fulfilled', value }))
        .catch(reason => ({ status: 'rejected', reason }))
    },
    restoreMemberSession () {
      try {
        const session = JSON.parse(window.localStorage.getItem(MEMBER_CODE_STORAGE_KEY) || '{}')
        this.sessionInfo = session || {}
        if (session) {
          this.memberForm.userName = session.userName || ''
          this.memberForm.mobile = session.mobile || ''
          this.memberForm.memberCode = ''
        }
      } catch (e) {
        this.sessionInfo = {}
      }
    },
    async loadExperts () {
      this.expertLoading = true
      try {
        const res = await fetchExpertList({ showOnHome: 1, pageNum: 1, pageSize: 4 })
        this.expertProfiles = res.rows
      } catch (err) {
        this.expertProfiles = []
      } finally {
        this.expertLoading = false
      }
    },
    async loadPortalContent () {
      this.newsLoading = true
      try {
        this.portalContent = await fetchPortalHomeContent(3)
      } catch (error) {
        console.error('加载门户内容失败', error)
        this.portalContent = {
          newsList: [],
          noticeList: [],
          enterpriseList: []
        }
      } finally {
        this.buildEnterpriseCards()
        this.newsLoading = false
      }
    },
    buildEnterpriseCards () {
      this.enterpriseCards = (this.portalContent.enterpriseList || []).slice(0, 3).map((item, index) => {
        const fallbackImage = this.enterpriseFallbackImages[index % this.enterpriseFallbackImages.length]
        const imageCandidates = item.coverImage ? resolvePortalFileUrlCandidates(item.coverImage) : []
        return {
          id: item.contentId,
          contentId: item.contentId,
          tag: item.industryType || '合作伙伴',
          title: item.title || '',
          displayMode: item.displayMode || 'article',
          externalUrl: item.externalUrl || '',
          action: '了解详情',
          rawCoverImage: item.coverImage || '',
          placeholderImage: fallbackImage,
          fallbackImage,
          displayImage: fallbackImage,
          imageCandidates,
          imageCandidateIndex: -1,
          isImageReady: false,
          isLoading: imageCandidates.length > 0
        }
      })

      this.preloadEnterpriseCardImages()
    },
    async preloadEnterpriseCardImages () {
      const preloadToken = ++this.enterprisePreloadToken
      const tasks = this.enterpriseCards.map((card, index) => this.preloadEnterpriseCardImage(card, index, preloadToken))
      await Promise.all(tasks.map(task => this.settlePromise(task)))
    },
    async preloadEnterpriseCardImage (card, index, preloadToken) {
      if (!card || !card.imageCandidates.length) {
        if (this.enterpriseCards[index]) {
          this.enterpriseCards[index].isLoading = false
        }
        return
      }

      try {
        const result = await preloadPortalImage(card.imageCandidates)
        if (preloadToken !== this.enterprisePreloadToken || !this.enterpriseCards[index]) {
          return
        }

        const targetCard = this.enterpriseCards[index]
        targetCard.isLoading = false

        if (result && result.url) {
          targetCard.displayImage = result.url
          targetCard.imageCandidateIndex = result.index
          targetCard.isImageReady = true
          return
        }

        targetCard.displayImage = targetCard.fallbackImage
        targetCard.isImageReady = false
        console.warn('合作伙伴封面图加载失败，已回退占位图', {
          contentId: targetCard.contentId,
          title: targetCard.title,
          coverImage: targetCard.rawCoverImage,
          candidates: targetCard.imageCandidates
        })
        return
      } catch (error) {
        if (preloadToken !== this.enterprisePreloadToken || !this.enterpriseCards[index]) {
          return
        }

        const targetCard = this.enterpriseCards[index]
        targetCard.isLoading = false
        targetCard.displayImage = targetCard.fallbackImage
        targetCard.isImageReady = false
        console.warn('合作伙伴封面图预加载异常，已回退占位图', {
          contentId: targetCard.contentId,
          title: targetCard.title,
          coverImage: targetCard.rawCoverImage,
          candidates: targetCard.imageCandidates,
          error: error && error.message ? error.message : error
        })
      }
    },
    async loadManagementDatabase () {
      try {
        const [dictRes, historyRes] = await Promise.all([
          this.settlePromise(fetchDatabaseDict('management_scope')),
          this.settlePromise(fetchInnovationsHistoryList({ pageNum: 1, pageSize: 2 }))
        ])
        this.managementCategoryOptions = dictRes.status === 'fulfilled' ? (dictRes.value.data || []) : []
        const historyRows = historyRes.status === 'fulfilled' ? (historyRes.value.rows || []) : []
        this.databaseEntries.management = historyRows.map(item => ({
          id: item.id,
          year: item.declareYear || '',
          title: item.resultsName || '',
          category: this.getManagementCategoryLabel(item.category),
          companyName: item.companyName || ''
        }))
        if (historyRes.status !== 'fulfilled') {
          throw historyRes.reason
        }
      } catch (error) {
        console.error('加载首页管理成果失败', error)
        this.databaseEntries.management = []
      }
    },
    async loadPaperDatabase () {
      try {
        const [dictRes, historyRes] = await Promise.all([
          this.settlePromise(fetchDatabaseDict('thesis_topic_type')),
          this.settlePromise(fetchThesisHistoryList({ pageNum: 1, pageSize: 3 }))
        ])
        this.paperTopicTypeOptions = dictRes.status === 'fulfilled' ? (dictRes.value.data || []) : []
        const historyRows = historyRes.status === 'fulfilled' ? (historyRes.value.rows || []) : []
        this.databaseEntries.paper = historyRows.map(item => ({
          id: item.id,
          year: item.declareYear || '',
          title: item.topic || '',
          category: this.getPaperTopicTypeLabel(item.topicType) || '-',
          author: this.formatPaperAuthors(item.authors) || '-'
        })).slice(0, 3)
        if (historyRes.status !== 'fulfilled') {
          throw historyRes.reason
        }
      } catch (error) {
        console.error('加载首页管理论文失败', error)
        this.databaseEntries.paper = []
      }
    },
    getManagementCategoryLabel (value) {
      if (value === undefined || value === null || value === '') {
        return ''
      }
      const match = this.managementCategoryOptions.find(item => String(item.dictValue) === String(value))
      return match ? match.dictLabel : value
    },
    getPaperTopicTypeLabel (value) {
      if (value === undefined || value === null || value === '') {
        return ''
      }
      const match = this.paperTopicTypeOptions.find(item => String(item.dictValue) === String(value))
      return match ? match.dictLabel : value
    },
    formatPaperAuthors (value) {
      if (!value) {
        return ''
      }
      if (typeof value !== 'string') {
        return ''
      }
      const trimmed = value.trim()
      if (!trimmed) {
        return ''
      }
      if (!trimmed.startsWith('[')) {
        return trimmed
      }
      try {
        const parsed = JSON.parse(trimmed)
        if (!Array.isArray(parsed)) {
          return ''
        }
        return parsed
          .map(item => (item && item.name ? String(item.name).trim() : ''))
          .filter(Boolean)
          .join('、')
      } catch (e) {
        return ''
      }
    },
    scrollToRouteSection () {
      if (!this.$route.query.section) {
        return
      }
      const el = document.getElementById(this.$route.query.section)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    },
    scrollToSection (id) {
      if (this.$route.path !== '/home' && this.$route.path !== '/') {
        this.$router.push({ path: '/home', query: { section: id } })
        return
      }
      this.$nextTick(() => {
        const el = document.getElementById(id)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' })
        }
      })
    },
    newsTabClass (category) {
      if (this.activeNewsCategory === category) {
        return 'bg-primary text-on-primary font-bold'
      }
      return 'text-on-surface font-medium hover:bg-surface-container-high'
    },
    databaseTabClass (tabKey) {
      if (this.activeDatabaseTab === tabKey) {
        return 'text-primary font-bold border-b-2 border-secondary pb-4 px-2 transition-colors'
      }
      return 'text-on-surface-variant font-medium hover:text-primary pb-4 px-2 transition-colors border-b-2 border-transparent'
    },
    buildDetailRoute (item) {
      if (this.activeDatabaseTab === 'management') {
        return { path: `/database/innovations/${item.id}` }
      }
      return { path: `/database/thesis/${item.id}` }
    },
    handleOpenDatabaseDetail (item) {
      this.pendingItem = item
      if (this.sessionInfo && this.sessionInfo.token) {
        this.$router.push(this.buildDetailRoute(item))
        return
      }
      this.memberError = ''
      this.showMemberDialog = true
      this.showMemberForm = false
    },
    closeMemberDialog () {
      this.showMemberDialog = false
      this.showMemberForm = false
      this.memberError = ''
    },
    async submitMemberCode () {
      this.memberError = ''
      if (!this.memberForm.memberCode || this.memberForm.memberCode.length !== 8) {
        this.memberError = '请输入8位会员码'
        return
      }
      try {
        const res = await loginByMemberCode({
          userName: this.memberForm.userName,
          mobile: this.memberForm.mobile,
          memberCode: this.memberForm.memberCode
        })
        this.sessionInfo = res.data || {}
        window.localStorage.setItem(MEMBER_CODE_STORAGE_KEY, JSON.stringify(this.sessionInfo))
        this.showMemberDialog = false
        this.showMemberForm = false
        if (this.pendingItem) {
          this.$router.push(this.buildDetailRoute(this.pendingItem))
        }
      } catch (e) {
        this.memberError = e.message || '会员码验证失败'
      }
    },
    openPortalItem (item) {
      if (item.displayMode === 'link' && item.externalUrl) {
        window.open(item.externalUrl, '_blank')
        return
      }
      this.$router.push(`/content/${item.contentId}`)
    },
    openEnterpriseItem (item) {
      if (!item) {
        return
      }
      if (item.displayMode === 'link' && item.externalUrl) {
        window.open(item.externalUrl, '_blank')
        return
      }
      if (!item.contentId) {
        return
      }
      this.$router.push(`/partner/${item.contentId}`)
    },
    goContentList (category) {
      this.$router.push({ path: '/content/list', query: { category } })
    },
    goAboutDetail (slug) {
      if (!slug) {
        this.goComingSoon()
        return
      }
      this.$router.push(`/about/${slug}`)
    },
    goContestHome () {
      this.$router.push('/contest/home')
    },
    goCaseCenterList () {
      this.$router.push('/case-center')
    },
    goCaseCenterDetail (id) {
      if (!id) {
        this.goCaseCenterList()
        return
      }
      this.$router.push(`/case-center/${id}`)
    },
    goJournalList () {
      this.$router.push('/journal')
    },
    goJournalDetail (id) {
      if (!id) {
        this.goJournalList()
        return
      }
      this.$router.push(`/journal/${id}`)
    },
    goComingSoon () {
      this.$router.push('/coming-soon')
    }
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/views/Home.vue